import { Box, Typography, Button, Card } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Checklist } from '@mui/icons-material';
import SurveyPreviewQuestions from './SurveyPreviewQuestions';
import { SurveyQuestion } from '../../types/survey';

const PreviewCard = styled(Card)(({ theme }) => ({
  border: '1px solid #EAECEF',
  borderRadius: theme.spacing(2),
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'column',
  position: 'sticky',
  top: theme.spacing(10),
  height: 'fit-content'
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8, 3),
  textAlign: 'center'
}));

const IconCircle = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: '#F8F9FA',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2)
}));

interface SurveyPreviewPanelProps {
  questions?: SurveyQuestion[];
  onSaveAndContinue: () => void;
  disabled?: boolean;
}

const SurveyPreviewPanel = ({
  questions = [],
  onSaveAndContinue,
  disabled = true
}: SurveyPreviewPanelProps) => {
  const isEmpty = questions.length === 0;
  return (
    <PreviewCard>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid #EAECEF' }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '1.125rem',
            color: 'text.primary'
          }}
        >
          Survey Preview
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flexGrow: 1, p: 3 }}>
        {isEmpty ? (
          <EmptyState>
            <IconCircle>
              <Checklist sx={{ fontSize: 40, color: '#9CA3AF' }} />
            </IconCircle>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.primary',
                mb: 0.5
              }}
            >
              Your survey is empty
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: 'text.secondary'
              }}
            >
              Choose a method to start adding questions.
            </Typography>
          </EmptyState>
        ) : (
          <SurveyPreviewQuestions questions={questions} />
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 3, borderTop: '1px solid #EAECEF' }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onSaveAndContinue}
          disabled={disabled}
          sx={{
            height: 44,
            backgroundColor: '#4F46E5',
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#4338CA',
              boxShadow: 'none'
            },
            '&.Mui-disabled': {
              backgroundColor: '#E5E7EB',
              color: '#9CA3AF'
            }
          }}
        >
          Save & Continue
        </Button>
      </Box>
    </PreviewCard>
  );
};

export default SurveyPreviewPanel;

